import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Flex, Tooltip } from 'antd';
import { type CSSProperties, type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type {
  AnalyticsWidgetStats,
  AnalyticsWidgetStatsRequest,
  WidgetEventType,
} from '@shared/api';
import type { WidgetChangesRequest, WidgetChangesTimeline } from '@shared/api/widget-changes-api';
import { useAppSelector } from '@shared/redux';
import { appSettingsSelectors } from '@shared/redux/ducks/app-settings';
import { AppTheme } from '@shared/services/theme';
import { Text } from '@shared/ui/antd';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';

import { formatTick } from '../lib/helpers';
import {
  formatConversion,
  mapWidgetSeriesToChartData,
  normalizeFunnelItems,
  WIDGET_EVENT_COLORS,
  WIDGET_EVENT_ORDER,
} from '../lib/widget-analytics';
import styles from '../analytics.module.scss';
import { WidgetChangesTimeline as WidgetChangesTimelineView } from './widget-changes-timeline';
import { WidgetPages } from './widget-pages/widget-pages';

type Props = {
  data?: AnalyticsWidgetStats;
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
  pagesRequest: AnalyticsWidgetStatsRequest | null;
  changesRequest: WidgetChangesRequest | null;
  changes?: WidgetChangesTimeline;
  changesLoading: boolean;
  changesError: boolean;
  onChangesRefetch: () => void;
  changesDemo: boolean;
};

const DEFAULT_VISIBLE_EVENTS: WidgetEventType[] = [
  'CHAT_OPEN',
  'FIRST_MESSAGE_SENT',
  'AUTH_FORM_SHOWN',
  'AUTH_FORM_SUBMIT',
];

export const WidgetAnalytics: FC<Props> = ({
  data,
  isLoading,
  isError,
  onRefetch,
  pagesRequest,
  changesRequest,
  changes,
  changesLoading,
  changesError,
  onChangesRefetch,
  changesDemo,
}) => {
  const { t } = useTranslation('translation');
  const themeMode = useAppSelector(appSettingsSelectors.selectTheme);
  const [visibleEvents, setVisibleEvents] = useState<WidgetEventType[]>(DEFAULT_VISIBLE_EVENTS);
  const [selectedChangeDate, setSelectedChangeDate] = useState<string | null>(null);

  const funnelItems = useMemo(() => normalizeFunnelItems(data?.funnel.items ?? []), [data]);
  const chartData = useMemo(() => mapWidgetSeriesToChartData(data?.dynamics), [data]);
  const markers = useMemo(() => {
    const dates = new Map(chartData.map((point) => [new Date(point.date).getTime(), point.date]));
    return (changes?.buckets ?? []).flatMap((bucket) => {
      const date = dates.get(new Date(bucket.date).getTime());
      return date ? [{ ...bucket, chartDate: date }] : [];
    });
  }, [changes, chartData]);
  const yAxisWidth = useMemo(() => {
    const longestValue = chartData.reduce((longest, point) => {
      const pointLongest = WIDGET_EVENT_ORDER.reduce(
        (currentLongest, eventType) =>
          Math.max(currentLongest, String(point[eventType] ?? 0).length),
        1,
      );

      return Math.max(longest, pointLongest);
    }, 1);

    return Math.max(28, longestValue * 8 + 12);
  }, [chartData]);
  const interval = data?.dynamics.interval ?? 'day';
  const isDark = themeMode === AppTheme.dark;

  const chartTooltipStyle: CSSProperties = {
    background: isDark ? '#1F232C' : '#FFFFFF',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.12)'}`,
    borderRadius: 8,
    boxShadow: isDark ? '0 12px 28px rgba(0, 0, 0, 0.42)' : '0 8px 20px rgba(0, 0, 0, 0.12)',
  };

  const handleEventVisibilityChange = (eventType: WidgetEventType, checked: boolean) => {
    setVisibleEvents((current) =>
      checked ? [...current, eventType] : current.filter((item) => item !== eventType),
    );
  };

  const history = (
    <WidgetChangesTimelineView
      request={changesRequest}
      data={changes}
      isLoading={changesLoading}
      isError={changesError}
      onRefetch={onChangesRefetch}
      selectedDate={selectedChangeDate}
      onSelectDate={setSelectedChangeDate}
      demo={changesDemo}
    />
  );

  if (isLoading)
    return (
      <Flex vertical gap={28}>
        <LoadingState />
        {history}
      </Flex>
    );
  if (isError)
    return (
      <Flex vertical gap={28}>
        <FetchingErrorState refetch={onRefetch} />
        {history}
      </Flex>
    );

  return (
    <Flex vertical gap={28}>
      <section>
        <Text variant="xl" fontWeight={500}>
          {t('analytics.widget.funnelTitle')}
        </Text>

        <div className={styles.widgetFunnel}>
          {funnelItems.map((item, index) => (
            <div key={item.event_type} className={styles.widgetFunnelCard}>
              <Flex align="start" justify="space-between" gap={12}>
                <Text variant="xl" fontWeight={500}>
                  {item.value}
                </Text>

                <Flex gap={8} className={styles.widgetConversions}>
                  {index > 0 && (
                    <Tooltip title={t('analytics.widget.conversionFromPrevious')}>
                      <span className={styles.widgetConversionPrevious}>
                        {formatConversion(item.conversion_from_previous)}
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={t('analytics.widget.conversionFromShown')}>
                    <span>{formatConversion(item.conversion_from_shown)}</span>
                  </Tooltip>
                </Flex>
              </Flex>

              <Flex align="center" gap={6} className={styles.widgetFunnelLabel}>
                <span>{t(`analytics.widget.events.${item.event_type}`)}</span>
                <Tooltip title={t(`analytics.widget.eventHints.${item.event_type}`)}>
                  <InfoCircleOutlined className={styles.widgetInfoIcon} />
                </Tooltip>
              </Flex>
            </div>
          ))}
        </div>
      </section>

      <section>
        <Text variant="xl" fontWeight={500}>
          {t('analytics.widget.dynamicsTitle')}
        </Text>

        <div className={styles.widgetLegend}>
          {WIDGET_EVENT_ORDER.map((eventType) => (
            <Checkbox
              key={eventType}
              className={styles.widgetSeriesCheckbox}
              checked={visibleEvents.includes(eventType)}
              data-checked={visibleEvents.includes(eventType)}
              style={{ '--widget-series-color': WIDGET_EVENT_COLORS[eventType] } as CSSProperties}
              onChange={(event) => handleEventVisibilityChange(eventType, event.target.checked)}
            >
              <span className={styles.widgetLegendLabel}>
                {t(`analytics.widget.events.${eventType}`)}
              </span>
            </Checkbox>
          ))}
        </div>

        <div className={styles.widgetChart}>
          {chartData.length === 0 && (
            <div className={styles.widgetChartEmpty}>{t('analytics.widget.noData')}</div>
          )}

          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 52, right: 34, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="var(--color-border-soft)" strokeDasharray="4 4" vertical />
              <XAxis
                dataKey="date"
                tickFormatter={(value: string) => formatTick(value, interval)}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--color-text-tertiary)', fontSize: 12 }}
              />
              <YAxis
                width={yAxisWidth}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                tick={{ fill: 'var(--color-text-tertiary)', fontSize: 12 }}
                domain={[0, 'auto']}
              />
              <ChartTooltip
                labelFormatter={(value) => formatTick(String(value), interval)}
                formatter={(value, name) => [value, t(`analytics.widget.events.${String(name)}`)]}
                contentStyle={chartTooltipStyle}
                labelStyle={{ color: 'var(--color-text-primary)' }}
                itemStyle={{ color: 'var(--color-text-secondary)' }}
                isAnimationActive={false}
              />

              {markers.map((bucket) => (
                <ReferenceLine
                  key={bucket.date}
                  x={bucket.chartDate}
                  stroke="#A78BFA"
                  strokeOpacity={0.55}
                  strokeWidth={1}
                  strokeDasharray="3 5"
                  label={{
                    content: ({ viewBox }) => {
                      if (!viewBox || !('x' in viewBox) || !('y' in viewBox)) return null;
                      const width = Math.max(38, String(bucket.change_count).length * 9 + 28);
                      return (
                        <g
                          transform={`translate(${Number(viewBox.x)}, ${Number(viewBox.y) + 10})`}
                          className={styles.chartChangeBadge}
                          role="button"
                          tabIndex={0}
                          aria-label={t('analytics.widget.changes.count', {
                            count: bucket.change_count,
                          })}
                          onClick={() => setSelectedChangeDate(bucket.date)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedChangeDate(bucket.date);
                            }
                          }}
                        >
                          <title>
                            {t('analytics.widget.changes.count', { count: bucket.change_count })}
                          </title>
                          <rect x={-width / 2} width={width} height={26} rx={8} />
                          <circle cx={-width / 2 + 10} cy={13} r={3} />
                          <text x={width / 2 - 9} y={17.5} textAnchor="end" fontSize={12}>
                            {bucket.change_count}
                          </text>
                        </g>
                      );
                    },
                  }}
                />
              ))}

              {WIDGET_EVENT_ORDER.map((eventType) =>
                visibleEvents.includes(eventType) ? (
                  <Line
                    key={eventType}
                    type="monotone"
                    dataKey={eventType}
                    stroke={WIDGET_EVENT_COLORS[eventType]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ) : null,
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <WidgetPages request={pagesRequest} enabled />

      {history}
    </Flex>
  );
};
